import { useMedia } from 'react-media';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, FormSection, Text, View, Box } from '../ui';

import type { PortCallData } from '../core/Core';
import type { SingleEvent } from '../core/TimeData';
import { isCargoEvent, CoreEventType } from '../core/TimeData';
import type {
  MrForm_CargoMeasurementFragment,
  RelatedOrgsQuery,
} from '../../graphql';
import { usePortCallsDocumentsQuery } from '../../graphql';
import NORForm from './NORForm';
import SOFForm from './SOFForm';
import BOLForm from './BOLForm';
import { get, isDefined, mapKeyBy } from '../../lib/util';
import PortCallFiles from './PortCallFiles';
import uniq from 'lodash/uniq';
import MRForm from './MRForm';
import { isCommitted } from '../Scenario';
import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import type { ShipmentModalNavTarget } from './shipment';
import { parseClientIdOpt } from '../core/ClientId';
import { hasUserRight, useCurrentUser } from '../../state/zustand';

export const PortCalls = ({
  onSelect,
  selectedPortCall,
  portCalls,
}: {
  onSelect: (portCallIndex: number) => void;
  selectedPortCall: number;
  portCalls: PortCallData[];
}): JSX.Element => {
  const isSmallScreen = useMedia({ query: '(max-width: 992px)' });

  return (
    <View
      width={isSmallScreen ? '100%' : '250'}
      flexDirection={isSmallScreen ? 'row' : 'column'}
    >
      {portCalls.map((call, index) => {
        const firstOrderTd = call.tds.find((td) => 'order' in td.event);
        if (!firstOrderTd) {
          console.error(`Port call without an order td`);
          return null;
        }
        const event = firstOrderTd.event;
        if (!('order' in event)) {
          throw new Error('cannot be');
        }

        const order = event.order;
        const loadEvent = event.type === CoreEventType.Load;
        const eventTypes = uniq(call.tds.map((td) => td.event.type))
          .sort()
          .filter(
            (x) => x === CoreEventType.Load || x === CoreEventType.Discharge,
          );
        const eventTypeLabel = eventTypes
          .map((x) => CoreEventType[x].toUpperCase())
          .join(', ');
        return (
          <Box
            key={`${index}-${event.category}-${event.entry.clientId}`}
            bg={
              selectedPortCall === index
                ? 'neutral.gray_150'
                : 'neutral.white_100'
            }
            mb={1}
            p={isSmallScreen ? 4 : 5}
            flex={isSmallScreen ? 1 : 0}
            justifyContent="center"
            onClick={() => onSelect(index)}
          >
            <Text fontWeight="bold">
              {loadEvent
                ? order.locationByLoadPortId?.name
                : order.locationByDischargePortId?.name}{' '}
              ({eventTypeLabel})
            </Text>
          </Box>
        );
      })}
    </View>
  );
};

interface PortCallsDocumentsProps {
  portCalls: PortCallData[];
  scenario: ScenarioSchedule;
  voyageName: string;
  navTarget?: ShipmentModalNavTarget;
  relatedOrgsData?: RelatedOrgsQuery | undefined;
}

const PortCallsDocuments = ({
  portCalls,
  scenario,
  navTarget,
  voyageName,
  relatedOrgsData,
}: PortCallsDocumentsProps): JSX.Element | null => {
  const portCallClientIds = portCalls
    .map((portCall) => portCall.id)
    .filter(isDefined);

  const orderIds = portCalls.reduce<Set<number>>((orderIds, portCall) => {
    portCall.tds.forEach(({ event }) => {
      if ('order' in event) orderIds.add(event.order.id);
    });

    return orderIds;
  }, new Set());

  const { data, refetch: refetchDocuments } = usePortCallsDocumentsQuery({
    variables: { portCallClientIds, orderIds: Array.from(orderIds) },
    fetchPolicy: 'cache-and-network',
  });

  const isSmallScreen = useMedia({ query: '(max-width: 992px)' });

  const currentUser = useCurrentUser();
  const userId = currentUser?.id ?? null;

  const prospectRef = useRef<HTMLDivElement | null>(null);
  const sofRef = useRef<HTMLDivElement | null>(null);
  const norRef = useRef<HTMLDivElement | null>(null);
  const bolRef = useRef<HTMLDivElement | null>(null);
  const mrRef = useRef<HTMLDivElement | null>(null);
  const activeSection = navTarget?.section || 'Prospects';

  useEffect(() => {
    if (activeSection === 'Prospects') prospectRef?.current?.scrollIntoView();
    else if (activeSection === 'SOF') sofRef?.current?.scrollIntoView();
    else if (activeSection === 'NOR') norRef?.current?.scrollIntoView();
    else if (activeSection === 'B/L') bolRef?.current?.scrollIntoView();
    else if (activeSection === 'MR') mrRef?.current?.scrollIntoView();
  }, [activeSection]);

  const defaultIndex = navTarget?.portLocation
    ? portCalls.findIndex((pc) =>
        pc.tds
          .map((td) => (td.event as SingleEvent).locationName)
          .includes(navTarget.portLocation),
      )
    : 0;

  const [selectedPortCallIndex, setSelectedPortCallIndex] = useState<number>(
    defaultIndex !== -1 ? defaultIndex : 0,
  );

  const portCall = get(portCalls, selectedPortCallIndex);
  const portCallClientId = portCall?.id ?? null;

  const portCallDataById = useMemo(
    () =>
      mapKeyBy(data?.allPortCalls?.nodes.filter(isDefined) ?? [], (p) => p.id),
    [data?.allPortCalls],
  );
  const portCallData = data?.allPortCalls?.nodes.find(
    (portCall) =>
      parseClientIdOpt(portCall?.clientId ?? null) === portCallClientId,
  );
  const portCallDbId = portCallData?.id;

  const organizationById = useMemo(
    () =>
      mapKeyBy(
        data?.allOrganizations?.nodes.filter(isDefined) ?? [],
        (x) => x.id,
      ),
    [data?.allOrganizations?.nodes],
  );
  const portCallFiles =
    portCallData?.portCallFilesByPortCallId.nodes.filter(isDefined) ?? [];
  const noticeOfReadiness =
    portCallData?.portNoticesByPortCallId.nodes[0] ?? undefined;
  const billOfLading =
    portCallData?.billOfLadingsByPortCallId.nodes[0] ?? undefined;
  const statementOfFacts =
    portCallData?.statementOfFactsByPortCallId.nodes[0] ?? undefined;
  const locationId = portCall?.locationId;
  const measurementReports =
    data?.allCargoMeasurements?.nodes.filter(
      (mr): mr is MrForm_CargoMeasurementFragment =>
        mr?.locationId === locationId,
    ) ?? [];
  const orderAttributes =
    data?.allOrderAttributes?.nodes.filter(isDefined) ?? [];

  const events = portCall?.tds.map((td) => td.event).filter(isCargoEvent) ?? [];

  const first = portCall?.tds[0].event;
  const portPta = first?.start;
  const portName =
    (first &&
      (first.type === CoreEventType.Load
        ? first.order.locationByLoadPortId?.name
        : first.type === CoreEventType.Discharge
          ? first.order.locationByDischargePortId?.name
          : first.type === CoreEventType.Single
            ? first.locationName
            : null)) ??
    'Port name N/A';

  const portTimeZone =
    (first &&
      (first.type === CoreEventType.Load
        ? first.order.locationByLoadPortId?.timezone
        : first.type === CoreEventType.Discharge
          ? first.order.locationByDischargePortId?.timezone
          : first.type === CoreEventType.Single
            ? first.locationTimeZone
            : null)) ??
    null;

  const ordersToLoad = events
    .filter((event) => event.type === CoreEventType.Load)
    .map((event) => event.order);

  // Needed for measurement reports
  const ordersToDischarge = events
    .filter((event) => event.type === CoreEventType.Discharge)
    .map((event) => event.order);

  const relatedOrgsById = useMemo(() => {
    return new Map(
      relatedOrgsData?.allOrders?.nodes.map((o) => [o?.id, o?.relatedOrgs]),
    );
  }, [relatedOrgsData]);

  const ordersToLoadOrgs = uniq(
    ordersToLoad.flatMap((o) => relatedOrgsById.get(o.id)),
  );
  const ordersToDischargeOrgs = uniq(
    ordersToDischarge.flatMap((o) => relatedOrgsById.get(o.id)),
  );

  const canEditPortCall = uniq([
    ...ordersToLoadOrgs,
    ...ordersToDischargeOrgs,
  ]).some((orgId) => orgId && hasUserRight(orgId, 'createAndEditPortCalls'));

  const canReadBOL = ordersToLoadOrgs.some(
    (orgId) => orgId && hasUserRight(orgId, 'accessBillOfLading'),
  );
  const canEditBOL = ordersToLoadOrgs.some(
    (orgId) => orgId && hasUserRight(orgId, 'createAndEditBillOfLading'),
  );
  const canReadMR = ordersToDischargeOrgs.some(
    (orgId) => orgId && hasUserRight(orgId, 'accessCargoMeasurement'),
  );
  const canEditMR = ordersToDischargeOrgs.some(
    (orgId) => orgId && hasUserRight(orgId, 'createAndEditCargoMeasurement'),
  );

  const error =
    typeof portCallDbId !== 'number'
      ? `You must commit these orders first before you can update
  the documents.`
      : events.some((ev) => !isCommitted(scenario, ev))
        ? `You must commit changes to assignments of these orders before you can update the documents.`
        : null;

  return (
    <View flex={1} overflowX="hidden">
      {portCalls && data ? (
        <View flex={1} flexDirection={isSmallScreen ? 'column' : 'row'}>
          <PortCalls
            onSelect={setSelectedPortCallIndex}
            portCalls={portCalls}
            selectedPortCall={selectedPortCallIndex}
          />
          <View
            flex={1}
            height="fit-content"
            borderLeftWidth="1px"
            borderLeftColor="neutral.gray_225"
          >
            {error || portCallDbId === undefined ? (
              <Form isForm={false} border="none">
                <FormSection title={error ?? 'Cannot view documents.'}>
                  {null}
                </FormSection>
              </Form>
            ) : (
              <>
                {userId && (
                  <Box ref={prospectRef}>
                    <PortCallFiles
                      portCallFiles={portCallFiles}
                      portCallId={portCallDbId}
                      portName={portName}
                      userId={userId}
                      readOnly={!canEditPortCall}
                      voyageName={voyageName}
                      onSave={() => refetchDocuments()}
                    />
                  </Box>
                )}
                {userId && (
                  <Box ref={norRef}>
                    <NORForm
                      portCallId={portCallDbId}
                      portName={portName}
                      portTimeZone={portTimeZone}
                      portPta={portPta}
                      nor={noticeOfReadiness}
                      userId={userId}
                      readOnly={!canEditPortCall}
                      onSave={() => refetchDocuments()}
                    />
                  </Box>
                )}
                {userId && (
                  <Box ref={sofRef}>
                    <SOFForm
                      portCallId={portCallDbId}
                      portName={portName}
                      portTimeZone={portTimeZone}
                      sof={statementOfFacts}
                      userId={userId}
                      readOnly={!canEditPortCall}
                      onSave={() => refetchDocuments()}
                    />
                  </Box>
                )}
                {userId && ordersToLoad.length > 0 && canReadBOL && (
                  <Box ref={bolRef}>
                    <BOLForm
                      portCallId={portCallDbId}
                      orders={ordersToLoad}
                      bol={billOfLading}
                      userId={userId}
                      organizations={organizationById}
                      orderAttributes={orderAttributes}
                      readOnly={!canEditBOL}
                      onSave={() => refetchDocuments()}
                    />
                  </Box>
                )}
                {userId && ordersToDischarge.length > 0 && canReadMR && (
                  <Box ref={mrRef}>
                    <MRForm
                      portCallId={portCallDbId}
                      orders={ordersToDischarge}
                      portcalls={portCallDataById}
                      measurements={measurementReports}
                      userId={userId}
                      organizations={organizationById}
                      onSave={() => refetchDocuments()}
                      locationId={locationId}
                      readOnly={!canEditMR}
                    />
                  </Box>
                )}
              </>
            )}
          </View>
        </View>
      ) : null}
    </View>
  );
};

export default PortCallsDocuments;
